import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { LazyMotion, motion } from 'framer-motion'
import { ArrowUpRight } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import { CalendarDots } from '@phosphor-icons/react/dist/ssr/CalendarDots'
import { Check } from '@phosphor-icons/react/dist/ssr/Check'
import { CopySimple } from '@phosphor-icons/react/dist/ssr/CopySimple'
import { FileDoc } from '@phosphor-icons/react/dist/ssr/FileDoc'
import { FileText } from '@phosphor-icons/react/dist/ssr/FileText'
import { Handshake } from '@phosphor-icons/react/dist/ssr/Handshake'
import { Question } from '@phosphor-icons/react/dist/ssr/Question'
import { ShieldCheck } from '@phosphor-icons/react/dist/ssr/ShieldCheck'
import { ShareNetwork } from '@phosphor-icons/react/dist/ssr/ShareNetwork'
import { Timer } from '@phosphor-icons/react/dist/ssr/Timer'
import promptfreePoster from '@/assets/moss-orb-terrarium-poster.avif'
import hykellLogo from '@/assets/hykell-logo.avif'
import goshaPortrait from '@/assets/portrait-gosha-front-80.avif'
import ottPortrait from '@/assets/ott-salmar-portrait.avif'
import rocketIcon from '@/assets/rocket.avif'
import wavingHand from '@/assets/waving-hand.avif'
import wildflowerFieldPoster from '@/assets/wildflower-field-poster.avif'
import { EditorialQuote } from '@/components/article/EditorialQuote'
import { CaseStudyFaqAccordion } from '@/components/case-study/CaseStudyFaqAccordion'
import { HomeAiCaseStudies } from '@/components/home/HomeAiCaseStudies'
import { SeoHead } from '@/components/seo/SeoHead'
import { siteConfig } from '@/config/site'
import { getAllCaseStudies, getAllNotes } from '@/data/contentService'
import { copyContactEmail } from '@/lib/copyContactEmail'

const pathname = '/hello/ott-salmar-418632'
const promptfreeVideoUrl = 'https://assets.gosha.ee/bgs/moss-orb-terrarium_web.mp4'
const offerVideoUrl = 'https://assets.gosha.ee/bgs/wildflower-field_web.mp4'
const loadDomAnimation = () =>
  import('@/lib/motion-dom-animation').then((mod) => mod.domAnimation)
const HomeTestimonials = lazy(() =>
  import('@/components/home/HomeTestimonials').then((mod) => ({ default: mod.HomeTestimonials }))
)

const readAlsoSlugs = [
  'united-accountants-custom-landing-pages-gtm-system',
  'automated-outbound-outreach-workflow',
  'personalized-outreach-visuals-workflow',
  'local-first-case-study-publishing-workflow',
]

const caseStudySlug = (cs: ReturnType<typeof getAllCaseStudies>[number]) =>
  cs.page.slug

const benefits = [
  {
    title: 'Easier internal forwarding',
    body: 'The first contact can forward one page instead of rewriting the conversation.',
    Icon: ShareNetwork,
  },
  {
    title: 'Cleaner restart later',
    body: 'Sales can restart later with context intact.',
    Icon: Timer,
  },
  {
    title: 'Less work for the client',
    body: 'The page carries summary, objections, missing inputs, and next step.',
    Icon: FileText,
  },
  {
    title: 'Next call starts further ahead',
    body: 'The next call starts from the blocker, not from zero.',
    Icon: ShieldCheck,
  },
]

const pilotSteps: ReactNode[] = [
  <>
    Forward this page to someone with{' '}
    <strong className="font-medium text-white">high-context sales calls and weak follow-up</strong>.
  </>,
  <>
    If Hykell later has this problem, we can test it with{' '}
    <strong className="font-medium text-white">one anonymised transcript</strong>.
  </>,
]

const faqItems = [
  {
    question: 'Who is this useful for?',
    answer:
      'B2B teams with high-context sales calls, long cycles, and buyers who need to forward the case internally.',
  },
  {
    question: 'When is this better than an email?',
    answer:
      'When the email would lose context: confirmed points, non-fit, objections, and what the buyer needs to forward.',
  },
  {
    question: 'What does sales need to provide?',
    answer:
      'A transcript or meeting notes, buyer context, seller notes, proof, and the intended next step.',
  },
  {
    question: 'What should be reviewed before sending?',
    answer:
      'Claims, numbers, sensitive details, open objections, and anything that sounds too confident.',
  },
  {
    question: 'What would a small test look like?',
    answer:
      'One qualified use case, one anonymised transcript, one private page, and one review.',
  },
]

const sectionTitleClass =
  "font-['Familjen_Grotesk'] text-[22px] font-normal leading-[1.12] tracking-[-0.2px] text-[var(--foreground)]"

const darkPrimaryCtaClass =
  'inline-flex w-fit shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-white px-4 py-2 text-[15px] font-medium tracking-[-0.15px] text-black [transition-property:background-color,transform] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:bg-white/92 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black'

const offerInlineIconClass = 'ml-1 inline-block size-4 -translate-y-px text-white/45'

const SectionHeader = ({ eyebrow, title }: { eyebrow?: string; title: ReactNode }) => (
  <div className="space-y-1">
    {eyebrow ? (
      <div className="text-[11px] font-semibold uppercase leading-4 tracking-[0.14em] text-[var(--foreground-subtle)]">
        {eyebrow}
      </div>
    ) : null}
    <h2 className={sectionTitleClass}>{title}</h2>
  </div>
)

const Section = ({
  eyebrow,
  title,
  children,
}: {
  eyebrow?: string
  title: ReactNode
  children: ReactNode
}) => (
  <section className="space-y-4">
    <SectionHeader eyebrow={eyebrow} title={title} />
    {children}
  </section>
)

const FollowUpPromptPromo = ({
  onCopyEmail,
  emailCopied,
}: {
  onCopyEmail: () => void
  emailCopied: boolean
}) => (
  <section aria-label="Referral and follow-up asset example">
    <div className="relative min-h-[133px] overflow-hidden rounded-[16px] bg-black px-4 py-3 text-white">
      <div className="pointer-events-none absolute top-0 left-[240px] h-full w-[239px] overflow-hidden">
        <img
          src={promptfreePoster}
          alt=""
          width={239}
          height={134}
          className="h-full w-full max-w-none scale-[1.35] object-cover md:hidden"
          decoding="async"
        />
        <video
          className="hidden h-full w-full max-w-none scale-[1.35] object-cover md:block"
          src={promptfreeVideoUrl}
          poster={promptfreePoster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        <div className="absolute inset-y-0 left-0 w-[131px] bg-gradient-to-r from-black to-transparent" />
      </div>

      <div className="relative z-10 flex max-w-[314px] flex-col items-start gap-[18px]">
        <p className="font-['Familjen_Grotesk'] text-[22px] font-normal leading-[23.76px] tracking-[-0.44px] text-white [text-wrap:pretty]">
          If a team like this comes to mind, send them this page
        </p>
        <p className="flex flex-nowrap items-center gap-x-2 text-[15px] leading-[22.5px] tracking-[-0.16px]">
          <a
            href={siteConfig.calendlyUrl}
            rel="noreferrer"
            target="_blank"
            className={darkPrimaryCtaClass}
            aria-label="Schedule a call (opens in new tab)"
          >
            Schedule a call
          </a>
          <span className="inline-flex items-center gap-x-2 whitespace-nowrap">
            <span className="font-medium text-white/55">or</span>
            <button
              type="button"
              onClick={onCopyEmail}
              className="group inline-flex items-center gap-1 font-medium text-white underline decoration-white/45 underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              aria-label={emailCopied ? 'Email copied' : 'Copy email address'}
            >
              copy email
              <span className="relative inline-flex size-5 items-center justify-center">
                <motion.span
                  aria-hidden
                  animate={{
                    opacity: emailCopied ? 0 : 1,
                    scale: emailCopied ? 0.25 : 1,
                    filter: emailCopied ? 'blur(4px)' : 'blur(0px)',
                  }}
                  transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                    <CopySimple size={16} weight="fill" className="text-white" />
                  </span>
                  <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-150 group-hover:opacity-0">
                    <CopySimple size={16} weight="regular" className="text-white" />
                  </span>
                </motion.span>
                <motion.span
                  aria-hidden
                  animate={{
                    opacity: emailCopied ? 1 : 0,
                    scale: emailCopied ? 1 : 0.25,
                    filter: emailCopied ? 'blur(0px)' : 'blur(4px)',
                  }}
                  transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <Check size={16} weight="bold" className="text-white" />
                </motion.span>
              </span>
            </button>
          </span>
        </p>
      </div>
    </div>
  </section>
)

export const OttSalmarFollowUpPage = () => {
  const [emailCopied, setEmailCopied] = useState(false)
  const [scheduleLinkHovered, setScheduleLinkHovered] = useState(false)
  const [avatarHovered, setAvatarHovered] = useState(false)
  const [avatarSwapped, setAvatarSwapped] = useState(false)
  const resetTimerRef = useRef<number | null>(null)
  const readAlsoCaseStudies = readAlsoSlugs
    .map((slug) => getAllCaseStudies().find((cs) => caseStudySlug(cs) === slug))
    .filter((cs): cs is ReturnType<typeof getAllCaseStudies>[number] => Boolean(cs))
  const readAlsoNotes = getAllNotes().slice(0, 2)

  useEffect(
    () => () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current)
      }
    },
    [],
  )

  const handleCopyEmail = async () => {
    const ok = await copyContactEmail()
    if (!ok) {
      return
    }
    setEmailCopied(true)
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current)
    }
    resetTimerRef.current = window.setTimeout(() => {
      setEmailCopied(false)
      resetTimerRef.current = null
    }, 1800)
  }

  return (
    <>
      <SeoHead pathname={pathname} kind="private-follow-up" lcpImageUrl={ottPortrait} />
      <div className="site-page mx-auto w-full max-w-[448px] px-4 pb-24 md:pt-28">
        <div className="space-y-14">
          <header className="space-y-6">
            <motion.button
              type="button"
              aria-label="Swap Ott portrait and Hykell logo"
              onClick={() => setAvatarSwapped((current) => !current)}
              onMouseEnter={() => setAvatarHovered(true)}
              onMouseLeave={() => setAvatarHovered(false)}
              onFocus={() => setAvatarHovered(true)}
              onBlur={() => setAvatarHovered(false)}
              whileTap={{ scale: 0.96 }}
              className="inline-flex h-[60px] w-10 cursor-pointer items-center justify-center self-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
            >
              <div className="relative h-[60px] w-10">
                <motion.img
                  src={ottPortrait}
                  alt="Ott Salmar"
                  width={40}
                  height={60}
                  animate={{
                    x: avatarSwapped ? 3 : 0,
                    y: avatarSwapped ? 3 : 0,
                    scale: avatarSwapped ? 0.94 : 1,
                    rotate: avatarSwapped ? (avatarHovered ? 6 : 2) : avatarHovered ? -6 : -1,
                  }}
                  transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
                  className="absolute inset-0 z-20 h-full w-full rounded-[6px] object-cover shadow-[0_7px_18px_rgba(0,0,0,0.14)]"
                  decoding="async"
                />
                <motion.span
                  aria-hidden
                  animate={{
                    x: avatarSwapped ? 0 : 3,
                    y: avatarSwapped ? 0 : 3,
                    scale: avatarSwapped ? 1 : 0.94,
                    rotate: avatarSwapped ? (avatarHovered ? -6 : -1) : avatarHovered ? 6 : 2,
                  }}
                  transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
                  className="absolute inset-0 flex h-full w-full items-center justify-center overflow-hidden rounded-[6px] bg-white shadow-[0_7px_18px_rgba(0,0,0,0.14)] ring-1 ring-black/5"
                  style={{ zIndex: avatarSwapped ? 20 : 10 }}
                >
                  <img
                    src={hykellLogo}
                    alt=""
                    width={40}
                    height={60}
                    className="h-full w-full object-cover"
                    decoding="async"
                  />
                </motion.span>
              </div>
            </motion.button>
            <div className="space-y-4">
              <h1 className="ui-heading-balance font-['Familjen_Grotesk'] text-[32px] font-normal leading-[1.02] tracking-[-0.32px] text-[var(--foreground)]">
                <img
                  src={wavingHand}
                  alt=""
                  width={32}
                  height={32}
                  className="rain-wave-icon mr-2 inline-block size-8"
                  aria-hidden="true"
                  decoding="async"
                />
                Ott, here is the follow-up format we discussed.
              </h1>
              <p className="text-[15px] leading-6 tracking-[-0.01em] text-[var(--foreground-muted)] [text-wrap:pretty]">
                One conversation turned into a private page the buyer can{' '}
                <strong className="font-medium text-[var(--foreground)]">forward internally</strong>,
                with the context, real value, objections, and next step still intact. If needed, you
                can{' '}
              <a
                href={siteConfig.calendlyUrl}
                rel="noreferrer"
                target="_blank"
                onMouseEnter={() => setScheduleLinkHovered(true)}
                onMouseLeave={() => setScheduleLinkHovered(false)}
                onFocus={() => setScheduleLinkHovered(true)}
                onBlur={() => setScheduleLinkHovered(false)}
                aria-label="Book next call (opens in new tab)"
                className="inline-flex items-center gap-1 font-medium text-[var(--foreground)] underline decoration-[#b7b7b7] underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
              >
                book next call
                <span className="relative inline-flex size-4 items-center justify-center">
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: scheduleLinkHovered ? 0 : 1,
                      scale: scheduleLinkHovered ? 0.25 : 1,
                      filter: scheduleLinkHovered ? 'blur(4px)' : 'blur(0px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <CalendarDots aria-hidden size={16} weight="regular" />
                  </motion.span>
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: scheduleLinkHovered ? 1 : 0,
                      scale: scheduleLinkHovered ? 1 : 0.25,
                      filter: scheduleLinkHovered ? 'blur(0px)' : 'blur(4px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <ArrowUpRight aria-hidden size={16} weight="regular" />
                  </motion.span>
                </span>
              </a>
              {' '}or here is my{' '}
              <button
                type="button"
                onClick={handleCopyEmail}
                aria-label={emailCopied ? 'Email copied' : 'Copy email address'}
                className="group inline-flex items-center gap-1 font-medium text-[var(--foreground)] underline decoration-[#b7b7b7] underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
              >
                email
                <span className="relative inline-flex size-5 items-center justify-center">
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: emailCopied ? 0 : 1,
                      scale: emailCopied ? 0.25 : 1,
                      filter: emailCopied ? 'blur(4px)' : 'blur(0px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                      <CopySimple size={16} weight="fill" />
                    </span>
                    <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-150 group-hover:opacity-0">
                      <CopySimple size={16} weight="regular" />
                    </span>
                  </motion.span>
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: emailCopied ? 1 : 0,
                      scale: emailCopied ? 1 : 0.25,
                      filter: emailCopied ? 'blur(0px)' : 'blur(4px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Check size={16} weight="bold" />
                  </motion.span>
                </span>
              </button>
              .
            </p>
            </div>
          </header>

          <Section
            eyebrow="What I heard"
            title={
              <>
                <img
                  src={hykellLogo}
                  alt=""
                  width={24}
                  height={24}
                  className="mr-1 inline-block size-6 -translate-y-0.5 rounded-[5px] object-cover ring-1 ring-black/5"
                  aria-hidden="true"
                  decoding="async"
                />
                Hykell has the company map. The people layer is harder.
              </>
            }
          >
            <div className="space-y-4 text-[15px] leading-6 text-[var(--foreground-muted)] [text-wrap:pretty]">
              <p>
                <strong className="font-medium text-[var(--foreground)]">
                  You already know the target companies and domains.
                </strong>
              </p>
              <p>
                The harder part is finding the right people inside them, then validating contact
                quality before enrichment.
              </p>
            </div>
          </Section>

          <section className="overflow-hidden rounded-[16px] bg-black text-white">
            <div className="space-y-4 px-4 pt-4 pb-0">
              <div className="space-y-1">
                <div className="text-[11px] font-semibold uppercase leading-4 tracking-[0.14em] text-white/45">
                  Solution demo
                </div>
                <h2 className="font-['Familjen_Grotesk'] text-[22px] font-normal leading-[1.12] tracking-[-0.2px] text-white [text-wrap:pretty]">
                  What the email usually loses.
                </h2>
              </div>
              <div className="rounded-[10px] border border-white/12 bg-white/7 text-[14px] leading-[21px] text-white/68">
                <div className="space-y-2 p-4">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white/42">to:</span>
                    <img
                      src={ottPortrait}
                      alt=""
                      width={20}
                      height={20}
                      className="size-5 rounded-full object-cover"
                      aria-hidden="true"
                      decoding="async"
                    />
                    <span>Ott · Hykell</span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white/42">from:</span>
                    <img
                      src={goshaPortrait}
                      alt=""
                      width={20}
                      height={20}
                      className="size-5 rounded-full object-cover"
                      aria-hidden="true"
                      decoding="async"
                    />
                    <span>gknjazev@gmail.com</span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div>
                    <span className="font-medium text-white/42">topic:</span>{' '}
                    <span>how one-to-one post-call pages work</span>
                  </div>
                </div>
                <div className="border-t border-white/10 p-4 text-[15px] leading-6 [text-wrap:pretty]">
                  <p>
                    Ott, here is the private follow-up page from our call:{' '}
                    <a
                      href={pathname}
                      className="font-medium text-white underline decoration-white/45 underline-offset-3 transition-[text-decoration-color] duration-150 hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                    >
                      open it
                    </a>
                    .
                  </p>
                </div>
              </div>
              <p className="text-[15px] font-medium leading-6 text-white [text-wrap:pretty]">
                After a sales call, context dies in the handoff. A post-call page can be a
                calculator
                <ShieldCheck className={offerInlineIconClass} weight="regular" aria-hidden />,
                resend page
                <Question className={offerInlineIconClass} weight="regular" aria-hidden />,
                decision doc
                <FileDoc className={offerInlineIconClass} weight="regular" aria-hidden />, or
                custom forwarding asset
                <ShareNetwork className={offerInlineIconClass} weight="regular" aria-hidden /> with
                the objection and next step
                <Handshake className={offerInlineIconClass} weight="regular" aria-hidden />.{' '}
                <strong>A transcript is too messy. A recap is too thin.</strong>
              </p>
            </div>
            <div className="relative h-[176px] overflow-hidden md:h-[220px]">
              <img
                src={wildflowerFieldPoster}
                alt=""
                width={960}
                height={540}
                className="h-full w-full object-cover md:hidden"
                aria-hidden="true"
                decoding="async"
              />
              <video
                src={offerVideoUrl}
                poster={wildflowerFieldPoster}
                className="hidden h-full w-full object-cover md:block"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black via-black/55 to-transparent" />
            </div>
          </section>

          <Section eyebrow="Benefits" title="Why this can still be useful when the sale is not today.">
            <div className="grid grid-cols-2 gap-2">
              {benefits.map((benefit) => (
                <article key={benefit.title} className="rounded-[10px] border border-[var(--border)] bg-white p-4">
                  <benefit.Icon
                    aria-hidden
                    className="mb-3 size-8 text-[var(--foreground-subtle)]"
                    weight="regular"
                  />
                  <h3 className="font-['Familjen_Grotesk'] text-[18px] font-normal leading-6 tracking-[-0.16px] text-[var(--foreground)]">
                    {benefit.title}
                  </h3>
                  <p className="mt-4 text-[14px] leading-[22px] text-[var(--foreground-muted)] [text-wrap:pretty]">
                    {benefit.body}
                  </p>
                </article>
              ))}
            </div>
          </Section>

          <div className="py-2">
            <EditorialQuote
              body="If we send them like a hyperpersonalized website that has all of their kind of key things, next steps lined out, that would be really cool."
              name="Ott Salmar"
              role="Hykell"
              photoUrl={ottPortrait}
            />
          </div>

          <Section
            eyebrow="Next step"
            title={
              <>
                <img
                  src={rocketIcon}
                  alt=""
                  width={24}
                  height={24}
                  className="rocket-shake-icon mr-2 inline-block size-6"
                  aria-hidden="true"
                  decoding="async"
                />
                Use this when the buyer needs to forward the case internally.
              </>
            }
          >
            <div className="space-y-3">
              {pilotSteps.map((item, index) => (
                <div key={index} className="flex items-center gap-3 rounded-[10px] border border-white/10 bg-black p-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white text-[12px] font-semibold leading-none text-black">
                    {index + 1}
                  </span>
                  <p className="text-[14px] leading-[22px] text-white/72">{item}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Questions from the call.">
            <CaseStudyFaqAccordion items={faqItems} />
          </Section>

          <FollowUpPromptPromo onCopyEmail={handleCopyEmail} emailCopied={emailCopied} />

          <div className="-mx-4">
            <LazyMotion features={loadDomAnimation} strict>
              <Suspense fallback={null}>
                <HomeTestimonials />
              </Suspense>
            </LazyMotion>
          </div>

          <HomeAiCaseStudies
            title="Read also"
            items={readAlsoCaseStudies}
            notes={readAlsoNotes}
            showExploreMore={false}
            padded={false}
            variant="readAlso"
          />
        </div>
      </div>
    </>
  )
}
